/**
 * Generic side-drawer controller.
 *
 * The desktop counterpart to bottom-drawer.js. Slides an `<aside>`
 * element in from the left or right edge, controlled via data attributes.
 *
 * Markup convention:
 *   <aside data-js="side-drawer" data-side="left|right" data-state="closed"
 *          id="my-drawer" aria-hidden="true" tabindex="-1">
 *     <header class="side-drawer-header">
 *       <h2 class="side-drawer-title">…</h2>
 *       <button class="side-drawer-close" data-side-drawer-close aria-label="Close">…</button>
 *     </header>
 *     <div class="side-drawer-body">…</div>
 *     <footer class="side-drawer-footer">…</footer>
 *   </aside>
 *
 * Triggers (declarative):
 *   [data-side-drawer-open="drawer-id"]  — opens the drawer with matching id
 *   [data-side-drawer-close]             — closes the closest parent drawer
 *
 * API (imperative):
 *   window.SideDrawer.open(el | id)
 *   window.SideDrawer.close(el | id)
 */
(function () {
  if (window.SideDrawer) return;

  /** @type {Map<string, HTMLElement|null>} Tracks the element that triggered each drawer open. */
  const triggerMap = new Map();

  /**
   * Resolve a drawer element from an ID string or element ref.
   * @param {string|HTMLElement} ref
   * @returns {HTMLElement|null}
   */
  function resolve(ref) {
    if (typeof ref === 'string') return document.getElementById(ref);
    return ref;
  }

  /**
   * Open a side drawer.
   * @param {string|HTMLElement} ref
   */
  function open(ref) {
    const aside = resolve(ref);
    if (!aside || aside.dataset.state === 'open') return;

    const id = aside.id || 'side-drawer';

    triggerMap.set(id, document.activeElement);

    aside.dataset.state = 'open';
    aside.setAttribute('aria-hidden', 'false');
    
    aside.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true, detail: { id } }));

    if (window.themeUtils) {
      window.themeUtils.lockScroll(id);
      window.themeUtils.emit('overlay-show', { source: id });
    }

    requestAnimationFrame(function () {
      const closeBtn = aside.querySelector('[data-side-drawer-close]');
      if (closeBtn) closeBtn.focus();
    });

    let fired = false;
    const onEnd = function (e) {
      if (e && e.type === 'transitionend' && e.target !== aside) return;
      if (fired) return;
      fired = true;
      aside.removeEventListener('transitionend', onEnd);
      if (aside.dataset.state === 'open') {
        aside.dispatchEvent(new CustomEvent('drawer:opened', { bubbles: true, detail: { id } }));
      }
    };
    aside.addEventListener('transitionend', onEnd);
    setTimeout(onEnd, 400);
  }

  /**
   * Close a side drawer.
   * @param {string|HTMLElement} ref
   */
  function close(ref, reason = 'api') {
    const aside = resolve(ref);
    if (!aside || aside.dataset.state !== 'open') return;

    const id = aside.id || 'side-drawer';

    aside.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true, detail: { id, reason } }));

    aside.dataset.state = 'closed';
    aside.setAttribute('aria-hidden', 'true');

    if (window.themeUtils) {
      window.themeUtils.unlockScroll(id);
      window.themeUtils.emit('overlay-hide', { source: id });
    }

    const trigger = triggerMap.get(id);
    if (trigger && typeof trigger.focus === 'function') {
      trigger.focus();
    }
    triggerMap.delete(id);

    let fired = false;
    const onEnd = function (e) {
      if (e && e.type === 'transitionend' && e.target !== aside) return;
      if (fired) return;
      fired = true;
      aside.removeEventListener('transitionend', onEnd);
      if (aside.dataset.state === 'closed') {
        aside.dispatchEvent(new CustomEvent('drawer:closed', { bubbles: true, detail: { id } }));
      }
    };
    aside.addEventListener('transitionend', onEnd);
    setTimeout(onEnd, 400);
  }

  /**
   * Set loading state on a side drawer.
   * @param {string|HTMLElement} ref
   * @param {boolean} isLoading
   */
  function setLoading(ref, isLoading) {
    const aside = resolve(ref);
    if (aside) {
      aside.dataset.loading = isLoading ? 'true' : 'false';
    }
  }

  /* ── Declarative triggers ────────────────────────── */

  document.addEventListener('click', function (e) {
    const openTrigger = e.target.closest('[data-side-drawer-open]');
    if (openTrigger) {
      e.preventDefault();
      open(openTrigger.dataset.sideDrawerOpen);
      return;
    }

    const closeTrigger = e.target.closest('[data-side-drawer-close]');
    if (closeTrigger) {
      e.preventDefault();
      const aside = closeTrigger.closest('[data-js="side-drawer"]');
      if (aside) close(aside, 'button');
      return;
    }

    /* Overlay click — the global page overlay carries [data-page-overlay]. */
    if (e.target.matches('[data-page-overlay]')) {
      const openDrawers = document.querySelectorAll(
        '[data-js="side-drawer"][data-state="open"]'
      );
      openDrawers.forEach(function(drawer) { close(drawer, 'backdrop'); });
    }
  });

  /* ── Keydown & Focus trap ────────────────────────────── */

  let releaseFocusTrap = null;

  document.addEventListener('keydown', function (e) {
    const openDrawers = document.querySelectorAll(
      '[data-js="side-drawer"][data-state="open"]'
    );
    if (openDrawers.length === 0) return;
    
    if (e.key === 'Escape') {
      openDrawers.forEach(function(drawer) { close(drawer, 'escape'); });
      return;
    }
  });

  document.addEventListener('drawer:opened', function (e) {
    if (!e.target.matches('[data-js="side-drawer"]')) return;
    const aside = document.getElementById(e.detail.id);
    if (aside && window.themeUtils && window.themeUtils.trapFocus) {
      if (releaseFocusTrap) releaseFocusTrap();
      releaseFocusTrap = window.themeUtils.trapFocus(aside);
    }
  });

  document.addEventListener('drawer:closed', function (e) {
    if (!e.target.matches('[data-js="side-drawer"]')) return;
    if (releaseFocusTrap) {
      releaseFocusTrap();
      releaseFocusTrap = null;
    }
  });

  window.SideDrawer = Object.freeze({ open: open, close: close, setLoading: setLoading });
})();
